/**
 * Utilidades de URLs amigables (SEO) para las rúbricas públicas.
 *
 * - `slugify`: kebab-case sin acentos, minúsculas, solo [a-z0-9-].
 * - Rúbrica: `/{cadena de categorías}/{slug}-{shortId}` EN LA RAÍZ del
 *   dominio (sin prefijo /rubripedia) donde shortId = primeros 8 hex del
 *   group_id (estable entre versiones → buen canonical).
 *   Los segmentos de categoría son decorativos/SEO — la resolución real es
 *   solo por el shortId final, así que un link viejo o con categoría
 *   renombrada sigue resolviendo y se canonicaliza con un redirect (ver
 *   src/app/[...chain]/page.tsx).
 * - Categoría: `{slug}-c{id}` (id entero → legible y sin colisiones).
 * - Etiqueta: `{slug}` (el nombre de la etiqueta ES el identificador).
 */

import { categorySlug } from "@/lib/utils/category";

export function slugify(input: string): string {
  return (input || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "") // quita acentos
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80) || "item";
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const isUuid = (s: string) => UUID_RE.test(s.trim());

/** Segmentos compartidos: cadena de categorías + slug propio + shortId del linaje. */
function rubricSlugSegments(categoryChain: { name: string; slug?: string | null }[], name: string, groupId: string): string {
  const catPart = categoryChain.length > 0 ? `${categorySlug(categoryChain)}/` : "";
  return `${catPart}${slugify(name)}-${groupId.replace(/-/g, "").slice(0, 8)}`;
}

/** URL pública canónica de una rúbrica — en la raíz del dominio. */
export function rubricPath(categoryChain: { name: string; slug?: string | null }[], name: string, groupId: string): string {
  return `/${rubricSlugSegments(categoryChain, name, groupId)}`;
}

/** Misma URL amigable pero dentro de la plataforma (vista logeada/preview anónima). */
export function dashboardRubricPath(categoryChain: { name: string; slug?: string | null }[], name: string, groupId: string): string {
  return `/dashboard/rubric/${rubricSlugSegments(categoryChain, name, groupId)}`;
}

/** Extrae el shortId (8 hex al final) de un slug de rúbrica. null si no hay. */
export function shortIdFromSlug(slug: string): string | null {
  const m = slug.match(/-([0-9a-f]{8})$/i);
  return m ? m[1].toLowerCase() : null;
}

/** URL amigable de una categoría. */
export function categoryPath(name: string, id: number): string {
  return `/rubripedia/categoria/${slugify(name)}-c${id}`;
}

/** Extrae el id entero de un slug de categoría (`…-c43`). null si no hay. */
export function categoryIdFromSlug(slug: string): number | null {
  const m = slug.match(/-c(\d+)$/);
  return m ? Number(m[1]) : null;
}

/** URL amigable de una etiqueta. */
export function tagPath(name: string): string {
  return `/rubripedia/etiqueta/${slugify(name)}`;
}
